/*
  <drop-video> -- the drops page video.

  A YouTube video (data-youtube-id) shows only its thumbnail until the play
  button is pressed, then swaps in YouTube's player, playing. The theme's
  stand-in clip plays in place and hands over to the browser's own controls.
  While either plays the card carries .is-playing, which hides the copy laid
  over the video on phones. Also fills in the stand-in clip's running time.
*/
if (!customElements.get('drop-video')) {
  class DropVideo extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-video-play]');
      if (!this.button) return;

      if (this.dataset.youtubeId) this.setUpYouTube();
      else this.setUpVideo();
    }

    setUpYouTube() {
      this.checkPoster();
      this.button.addEventListener('click', () => this.playYouTube(), { once: true });
    }

    // YouTube answers for a missing full-size thumbnail with a 120px grey
    // placeholder (or an error), so fall back to the smaller one every
    // public video has. A private video has neither: the card is then left
    // dark, with just the play button.
    checkPoster() {
      const poster = this.querySelector('[data-video-poster]');
      const fallback = poster?.dataset.fallbackSrc;
      if (!fallback) return;

      const useFallback = () => {
        if (poster.src !== fallback) poster.src = fallback;
        else poster.remove();
      };
      const check = () => {
        if (poster.naturalWidth <= 120) useFallback();
      };

      poster.addEventListener('load', check);
      poster.addEventListener('error', useFallback);
      if (poster.complete) check();
    }

    playYouTube() {
      const iframe = document.createElement('iframe');
      iframe.className = 'vi-drop-video__player';
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(
        this.dataset.youtubeId
      )}?autoplay=1&rel=0&playsinline=1`;
      iframe.title = this.dataset.videoTitle || 'YouTube video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;

      this.querySelector('[data-video-poster]')?.remove();
      this.querySelector('[data-video-media]').prepend(iframe);
      this.classList.add('is-playing');
    }

    setUpVideo() {
      this.video = this.querySelector('[data-video]');
      if (!this.video) return;

      this.showLength();

      this.button.addEventListener('click', () => {
        this.video.controls = true;
        this.video.play();
      });
      this.video.addEventListener('play', () => this.classList.add('is-playing'));
      this.video.addEventListener('ended', () => {
        this.classList.remove('is-playing');
        this.video.controls = false;
        this.video.load();
      });
    }

    // Fills in the stand-in clip's running time -- "0:12 min." -- as the
    // server can't.
    showLength() {
      const length = this.querySelector('[data-video-length]');
      if (!length || length.textContent.trim()) return;

      const fill = () => {
        const total = Math.round(this.video.duration);
        if (!Number.isFinite(total) || total <= 0) return;
        const minutes = Math.floor(total / 60);
        const seconds = String(total % 60).padStart(2, '0');
        length.textContent = `${minutes}:${seconds} min.`;
        length.hidden = false;
      };

      if (this.video.readyState >= 1) fill();
      else this.video.addEventListener('loadedmetadata', fill, { once: true });
    }
  }

  customElements.define('drop-video', DropVideo);
}
